import React, { CSSProperties, useState, useMemo, useCallback } from "react";
import * as Mui from "@mui/material";
import { TableRow } from "./table-row";
import { SortCell } from "./sort-cell";
import { sortComparator } from "shared/sort-comparator";
import { TableCell } from "./table-cell";
import { TablePagination } from "./table-pagination";

interface Props<I> {
	[key: string]: any;
	items: I[];
	columns: Column<I>[];
	hidePagination?: boolean;
	page?: number;
	onPageChange?: (page: number) => void;
	pageSize?: number;
	onPageSizeChange?: (pageSize: number) => void;
	sort?: Sort;
	onSortChange?: (sort: Sort) => void;
	isSelected?: (item: I) => boolean;
	onSelect?: (items: I[]) => void;
	onUnselect?: (items: I[]) => void;
	fixedHeight?: number;
	rowClassName?: (item: I, row: number) => string | undefined;
	rowStyle?: (item: I, row: number) => CSSProperties | undefined;
	tableContainerProps?: Mui.TableContainerProps;
}

export interface Column<I> {
	[key: string]: any;
	id?: string;
	hidden?: boolean;
	title?: React.ReactNode;
	value: (item: I, column: number, row: number) => React.ReactNode;
	sort?: (a: I, b: I) => number;
	// rows with no value to sort by, kept last in both directions
	isBlank?: (item: I) => boolean;
	width?: CSSProperties["width"];
	align?: Mui.TableCellProps["align"];
	showOnHover?: boolean;
}

export interface Sort {
	id: string;
	desc: boolean;
}

const getPageSizeOptions = (pageSize: number) => {
	const baseOptions = [5, 10, 20, 25, 50, 100];
	const extraOptions = [250, 500];
	const options = [...baseOptions];

	extraOptions.forEach((opt) => {
		if (pageSize > 100 || pageSize === opt) {
			options.push(opt);
		}
	});

	return options.map((value) => ({
		value,
		label: `${value} rows`,
	}));
};

const renderTitleCell = <I,>(
	column: Column<I>,
	index: number,
	sort: Sort | undefined,
	onSortChange: (sort: Sort) => void
) => {
	if (column.sort && column.id) {
		const desc = sort && sort.id === column.id ? sort.desc : undefined;
		return (
			<SortCell
				key={index}
				style={{
					width: column.width,
				}}
				align={column.align}
				desc={desc}
				onClick={() => {
					if (!column.id) {
						throw new Error();
					}
					onSortChange({
						id: column.id,
						desc: !desc,
					});
				}}
			>
				{column.title}
			</SortCell>
		);
	} else {
		return (
			<TableCell
				key={index}
				style={{
					width: column.width,
				}}
				align={column.align}
			>
				{column.title}
			</TableCell>
		);
	}
};

export function Table<I>(props: Props<I>) {
	const {
		items: propItems,
		columns: propColumns,
		hidePagination,
		page: propPage,
		onPageChange,
		pageSize: propPageSize,
		onPageSizeChange,
		sort: propSort,
		onSortChange,
		isSelected,
		onSelect,
		onUnselect,
		fixedHeight,
		rowClassName,
		rowStyle,
		tableContainerProps,
	} = props;

	const [internalPage, setInternalPage] = useState(0);
	const [internalPageSize, setInternalPageSize] = useState(10);
	const [internalSort, setInternalSort] = useState<Sort | undefined>(undefined);

	const page = propPage ?? internalPage;
	const pageSize = propPageSize ?? internalPageSize;
	const sort = propSort ?? internalSort;

	const columns = useMemo(
		() => propColumns.filter((column) => !column.hidden),
		[propColumns]
	);
	const pageSizeOptions = useMemo(
		() => getPageSizeOptions(pageSize),
		[pageSize]
	);

	const sortedItems = useMemo(() => {
		let items = [...propItems];
		if (sort) {
			const column = columns.find((column) => column.id === sort.id);
			if (column && column.sort) {
				items = items.sort(sortComparator(column.sort, sort.desc, column.isBlank));
			}
		}
		return items;
	}, [propItems, columns, sort]);

	// With the pager hidden and no page size given, nothing tells the table to stop at 10 (MAX-4610):
	// show every row. A caller that hides the pager and passes a page size still gets that page.
	const showAll = !!hidePagination && propPageSize === undefined;
	const subItems = useMemo(
		() => (showAll ? sortedItems : sortedItems.slice(page * pageSize, page * pageSize + pageSize)),
		[sortedItems, page, pageSize, showAll]
	);

	const handlePageChange = useCallback(
		(_event: unknown, newPage: number) => {
			if (onPageChange) {
				onPageChange(newPage);
			} else {
				setInternalPage(newPage);
			}
		},
		[onPageChange]
	);

	const handlePageSizeChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const newSize = parseInt(event.target.value, 10);
			if (onPageSizeChange) {
				onPageSizeChange(newSize);
			} else {
				setInternalPageSize(newSize);
			}
		},
		[onPageSizeChange]
	);

	const handleSortChange = useCallback(
		(newSort: Sort) => {
			if (onSortChange) {
				onSortChange(newSort);
			} else {
				setInternalSort(newSort);
			}
		},
		[onSortChange]
	);

	return (
		<Mui.Grid container direction="column" spacing={2}>
			<Mui.Grid
				item
				style={
					fixedHeight
						? {
								width: "100%",
								overflowX: "auto",
								overflowY: "hidden",
								height: `calc(100vh - ${fixedHeight}px)`,
						  }
						: {}
				}
			>
				<Mui.TableContainer
					{...tableContainerProps}
					style={fixedHeight ? { height: `calc(100vh - ${fixedHeight}px)` } : {}}
				>
					<Mui.Table size="small">
						<Mui.TableHead>
							<Mui.TableRow>
								{isSelected && onSelect && onUnselect && (
									<TableCell style={{ width: 70 }}>
										<Mui.Checkbox
											style={{ marginLeft: 10 }}
											checked={
												subItems.length > 0 && subItems.every((item) => isSelected(item))
											}
											onChange={() => {
												if (subItems.every((item) => isSelected(item))) {
													onUnselect(subItems);
												} else {
													onSelect(subItems);
												}
											}}
										/>
									</TableCell>
								)}
								{columns.map((column, index) =>
									renderTitleCell(column, index, sort, handleSortChange)
								)}
							</Mui.TableRow>
						</Mui.TableHead>
						<Mui.TableBody>
							{subItems.map((item, row) => (
								<TableRow
									key={row}
									className={rowClassName ? rowClassName(item, row) : undefined}
									style={rowStyle ? rowStyle(item, row) : undefined}
								>
									{isSelected && onSelect && onUnselect && (
										<TableCell>
											<Mui.Checkbox
												style={{ marginLeft: 10 }}
												checked={isSelected(item)}
												onChange={() => {
													if (isSelected(item)) {
														onUnselect([item]);
													} else {
														onSelect([item]);
													}
												}}
											/>
										</TableCell>
									)}
									{columns.map((column, index) => (
										<TableCell key={index} align={column.align}>
											{column.showOnHover ? (
												<div className="showOnHover">{column.value(item, index, row)}</div>
											) : (
												column.value(item, index, row)
											)}
										</TableCell>
									))}
								</TableRow>
							))}
						</Mui.TableBody>
					</Mui.Table>
				</Mui.TableContainer>
			</Mui.Grid>
			{!!!hidePagination && (
				<Mui.Grid item>
					<TablePagination
						count={propItems.length}
						page={page}
						rowsPerPage={pageSize}
						rowsPerPageOptions={pageSizeOptions}
						onPageChange={handlePageChange}
						onRowsPerPageChange={handlePageSizeChange}
					/>
				</Mui.Grid>
			)}
		</Mui.Grid>
	);
}
